'use client';
import * as React from 'react';
import { useEffect, useState } from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useLocale } from 'next-intl';

import { LOCALE } from '@/config';
import { cn } from '@/lib/utils';
import { Language } from '@/types/language';

interface ChangeLanguageButtonProps {
    width?: number;
    height?: number;
    className?: string;
    languages: Language[];
}

export function ChangeLanguageButton({ width = 40, height = 40, className, languages }: ChangeLanguageButtonProps) {
    const locale = useLocale();
    const pathname = usePathname();
    const [targetLocale, setTargetLocale] = useState<string | null>(null);

    // Default next locale from config
    const defaultNextLocale = LOCALE.locale[locale]?.key || (locale === 'en' ? 'az' : 'en');

    useEffect(() => {
        // Check if default next locale exists in active languages
        const nextLocaleExists = languages.some(lang => lang.locale === defaultNextLocale && lang.active);

        // If the default next locale exists, set it as target
        if (nextLocaleExists) {
            setTargetLocale(defaultNextLocale);
        } else if (languages.length > 0) {
            // Otherwise set first active language that's not current locale
            const alternativeLocale = languages.find(lang => lang.locale !== locale && lang.active);
            if (alternativeLocale) {
                setTargetLocale(alternativeLocale.locale);
            }
        }
    }, [languages, locale, defaultNextLocale]);

    // en: Extract the path without the current locale
    const pathnameParts = pathname.split('/');
    // en: Remove the empty string and locale
    const pathWithoutLocale = pathnameParts.slice(2).join('/');

    // en: Create a new path with the new locale
    // en: If pathWithoutLocale is empty (main page), do not add an additional slash
    const href = targetLocale
        ? pathWithoutLocale
            ? `/${targetLocale}/${pathWithoutLocale}`
            : `/${targetLocale}`
        : '#';

    // If no valid target locale is found, don't render the button
    if (!targetLocale) {
        return null;
    }

    return (
        <a href={href} style={{ display: 'block' }}>
            <Image
                className={cn('cursor-pointer rounded-full lg:block', className)}
                src={languages.find(lang => lang.locale === locale)?.image || ''}
                alt={languages.find(lang => lang.locale === locale)?.title || locale.toUpperCase()}
                width={width}
                height={height}
            />
        </a>
    );
}
